import { useLocalization } from '@optimus-web/localization';
import {
    buildAccessibleLabel,
    useCharacterCountDebouncing,
    buildAccessibilityMarkup,
    buildCharacterCountContent,
} from '../Field/fieldAccessibilityUtils';
import type { TextareaElementProps } from './TextareaElement';

type TextareaAccessibilityProps = Pick<TextareaElementProps, 'name' | 'label' | 'helper' | 'error' | 'ariaLabel'> & {
    maxCharacterCount?: number;
    value?: string;
};

interface TextareaAccessibilityResult {
    accessibleLabelId: string | undefined;
    accessibleDescriptionId: string | undefined;
    debouncedCharacterCount: number;
    accessibilityMarkup: React.ReactNode;
}

export const useTextareaAccessibility = ({
    name,
    label,
    helper,
    error,
    maxCharacterCount,
    value,
    ariaLabel,
}: TextareaAccessibilityProps): TextareaAccessibilityResult => {
    const { t } = useLocalization();

    const debouncedCharacterCount = useCharacterCountDebouncing(value);

    const accessibleLabelId = label && ariaLabel ? `${name}-sr-only-label` : undefined;
    const accessibleLabelContent = label && ariaLabel ? buildAccessibleLabel([label, ariaLabel]) : null;

    const accessibleDescriptionId = helper || error || maxCharacterCount ? `${name}-sr-only-description` : undefined;
    const accessibleCharacterCountContent = buildCharacterCountContent(maxCharacterCount, debouncedCharacterCount, t);

    const accessibleDescriptionContent = buildAccessibleLabel([helper, error, accessibleCharacterCountContent]);

    const accessibilityMarkup = buildAccessibilityMarkup(
        accessibleLabelId,
        accessibleLabelContent,
        accessibleDescriptionId,
        accessibleDescriptionContent,
        accessibleCharacterCountContent
    );

    return {
        accessibleLabelId,
        accessibleDescriptionId,
        debouncedCharacterCount,
        accessibilityMarkup,
    };
};
